import { AddCircleOutline, MedicalServices, Warning } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Select, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { modalDivergences } from "../../atoms/atom";
import { ComparationService } from "../../services/ComparationService";
import { EmployeeService } from "../../services/EmployeeService";
import { AbsenceOption } from "../../types/AbsenceOption";
import { Comparation } from "../../types/Comparation";
import { ComparationChange } from "../../types/ComparationChange";
import { ComparationValues } from "../../types/ComparationValues";
import { ExamValidation } from "../../types/ExamValidation";
import { PositionDTO } from "../../types/PositionDTO";
import { RiskChangeValidation } from "../../types/RiskChangeValidation";
import { SectorOption } from "../../types/SectorOption";
import { useUtils } from "../../utils/utilFunctions";
import { useAlert } from "../contextProviders/AlertProvider";
import ComparationEventSelector from "./ComparationEventSelector";
import ModalCreatePosition from "./ModalCreatePosition";
import "./ModalDivergences.scss";

interface Props {
	comparation: Comparation | undefined;
	onSynced: () => void;
}

type DivergentField = {
	label: string;
	key: keyof ComparationValues;
	divergent: boolean;
};

const CREATE_POSITION = 'CREATE_POSITION';
const dismissedSituations = ['demitido', 'desligado'];
const pairedFields = [['situation', 'dismissalDate'], ['position', 'cbo']];
const actionMessages: { [key: string]: string } = {
	DISMISSAL: 'Será realizado o desligamento do funcionário no SGG',
	ABSENCE: 'Será registrado o afastamento do funcionário no SGG',
	ABSENCE_RETURN: 'Será registrado o retorno do afastamento do funcionário no SGG',
	POSITION_CHANGE: 'Será realizada a alteração do cargo do funcionário no SGG',
	MANUAL_POSITION_CHANGE: 'O setor e o cargo escolhidos serão gravados na atualização cadastral do'
		+ ' funcionário no SGG, sem registrar uma movimentação de cargo',
	TRANSFER: 'Será realizada a transferência do funcionário no SGG',
	UPDATE: 'Serão atualizados os dados cadastrais do funcionário no SGG',
	CANDIDATE_REMOVAL: 'O funcionário será removido de candidato no SGG'
};

const ModalDivergences: FC<Props> = (props: Props) => {
	const [open, setOpen] = useAtom(modalDivergences);
	const [selectedFields, setSelectedFields] = useState<string[]>([]);
	const [selectedAbsence, setSelectedAbsence] = useState<number>();
	const [selectedContractChange, setSelectedContractChange] = useState<number>();
	const [selectedDismissal, setSelectedDismissal] = useState<number>();
	const [selectedPosition, setSelectedPosition] = useState<number | string>('');
	const [positions, setPositions] = useState<PositionDTO[]>([]);
	const [loadingPositions, setLoadingPositions] = useState(false);
	const [positionSectors, setPositionSectors] = useState<SectorOption[]>([]);
	const [selectedPositionSector, setSelectedPositionSector] = useState<number | string>('');
	const [positionSectorsLoaded, setPositionSectorsLoaded] = useState(false);
	const [loadingPositionSectors, setLoadingPositionSectors] = useState(false);
	const [manualPositionChange, setManualPositionChange] = useState(false);
	const [createPositionOpen, setCreatePositionOpen] = useState(false);
	const [createPositionTransfer, setCreatePositionTransfer] = useState(false);
	const [transferSectors, setTransferSectors] = useState<SectorOption[]>([]);
	const [transferPositions, setTransferPositions] = useState<PositionDTO[]>([]);
	const [selectedTransferSector, setSelectedTransferSector] = useState<number | string>('');
	const [selectedTransferPosition, setSelectedTransferPosition] = useState<number | string>('');
	const [sectorsLoaded, setSectorsLoaded] = useState(false);
	const [loadingSectors, setLoadingSectors] = useState(false);
	const [loadingTransferPositions, setLoadingTransferPositions] = useState(false);
	const [sggAbsences, setSggAbsences] = useState<AbsenceOption[]>([]);
	const [selectedSggAbsence, setSelectedSggAbsence] = useState<number | string>('');
	const [sggAbsencesLoaded, setSggAbsencesLoaded] = useState(false);
	const [loadingSggAbsences, setLoadingSggAbsences] = useState(false);
	const [examValidation, setExamValidation] = useState<ExamValidation>();
	const [examChecked, setExamChecked] = useState(false);
	const [loadingExam, setLoadingExam] = useState(false);
	const [riskChange, setRiskChange] = useState<RiskChangeValidation>();
	const [loadingRiskChange, setLoadingRiskChange] = useState(false);
	const [saving, setSaving] = useState(false);
	const { showAlert, showError } = useAlert();
	const { formatIsoDate, formatCbo } = useUtils();
	const comparationService = new ComparationService();
	const employeeService = new EmployeeService();

	const divergentFields = () => {
		const comparation = props.comparation;
		if (!comparation) {
			return [];
		}
		const allFields: DivergentField[] = [
			{ label: 'Nome', key: 'name', divergent: comparation.divergentNome },
			{ label: 'Cargo', key: 'position', divergent: comparation.divergentCargo },
			{ label: 'CPF', key: 'cpf', divergent: comparation.divergentCpf },
			{ label: 'Data de admissão', key: 'admissionDate', divergent: comparation.divergentDataAdmissao },
			{ label: 'Data de desligamento', key: 'dismissalDate', divergent: comparation.divergentDataDesligamento },
			{ label: 'Data de nascimento', key: 'birthDate', divergent: comparation.divergentDataNascimento },
			{ label: 'CBO', key: 'cbo', divergent: comparation.divergentDescricaoCbo },
			{ label: 'Local de trabalho', key: 'workplace', divergent: comparation.divergentLocalTrabalho },
			{ label: 'Matrícula', key: 'registration', divergent: comparation.divergentMatricula },
			{ label: 'Situação', key: 'situation', divergent: comparation.divergentSituacao }
		];
		return allFields.filter(field => field.divergent);
	};

	const changesByType = (eventType: string): ComparationChange[] => {
		const changes = props.comparation?.changes;
		return changes ? changes.filter(change => change.eventType === eventType) : [];
	};

	const normalize = (value: string | undefined) => (value || '').trim().toLowerCase();

	const normalizePositionName = (value: string | undefined) => (value || '').trim()
		.replace(/\s+/g, ' ').toLowerCase();

	const matchesEsocialPosition = (position: PositionDTO) => {
		const esocialPosition = normalizePositionName(props.comparation?.esocialValues?.position);
		return !!esocialPosition && (normalizePositionName(position.cargo) === esocialPosition
			|| normalizePositionName(position.funcao) === esocialPosition);
	};

	const formatValue = (key: string, value: string | undefined) => key === 'cbo'
		? formatCbo(value || '-')
		: formatIsoDate(value || '-');

	const toggleField = (key: string) => {
		const pair = pairedFields.find(item => item.includes(key));
		const keys = pair
			? pair.filter(item => fields.some(field => field.key === item))
			: [key];
		setSelectedFields(prev => prev.includes(key)
			? prev.filter(item => !keys.includes(item))
			: [...prev, ...keys.filter(item => !prev.includes(item))]
		);
	};

	const fieldGroup = (key: string) => {
		if (key === 'situation') {
			return 'SITUATION';
		}
		if (key === 'dismissalDate') {
			return fields.some(field => field.key === 'situation') ? 'SITUATION' : 'UPDATE';
		}
		if (key === 'position' || key === 'cbo') {
			return 'POSITION';
		}
		if (key === 'workplace') {
			return 'WORKPLACE';
		}
		return 'UPDATE';
	};

	const isFieldLocked = (key: string) => key === 'cbo' && missingEsocialCbo
		&& !fields.some(field => field.key === 'position');

	const fieldTooltip = (key: string) => {
		if (isFieldLocked(key)) {
			return 'O eSocial não informou o CBO deste funcionário, então não há cargo com esse CBO'
				+ ' para selecionar. A divergência pode ser ignorada.';
		}
		return !!activeGroup && activeGroup !== fieldGroup(key)
			? 'É permitida uma ação por atualização'
			: '';
	};

	const getPositionSectors = async () => {
		const workplace = props.comparation?.sggValues?.workplace;
		if (!workplace) {
			setPositionSectorsLoaded(true);
			return;
		}
		setLoadingPositionSectors(true);
		const response = await comparationService.getSectorOptions(props.comparation!.id, workplace);
		setPositionSectors(response && response.data ? response.data : []);
		setPositionSectorsLoaded(true);
		setLoadingPositionSectors(false);
	};

	const getPositions = async (sectorId: string) => {
		if (!props.comparation) {
			return [];
		}
		setLoadingPositions(true);
		const response = await comparationService.getPositions(props.comparation.id, Number(sectorId));
		const list = response && response.data ? response.data : [];
		setPositions(list);
		setLoadingPositions(false);
		return list;
	};

	const handleChangePositionSector = (sectorId: string) => {
		setSelectedPositionSector(sectorId);
		setSelectedPosition('');
		setPositions([]);
		getPositions(sectorId);
	};

	const handleSelectPosition = (value: string, transfer: boolean) => {
		if (value === CREATE_POSITION) {
			setCreatePositionTransfer(transfer);
			setCreatePositionOpen(true);
			return;
		}
		if (transfer) {
			setSelectedTransferPosition(value);
			return;
		}
		setSelectedPosition(value);
	};

	const handleCreatedPosition = async (positionId: number, targetSectorId: number) => {
		setCreatePositionOpen(false);
		if (createPositionTransfer) {
			setSelectedTransferPosition('');
			setTransferPositions([]);
			setSelectedTransferSector(targetSectorId);
			const list = await getTransferPositions(String(targetSectorId));
			if (list.some(item => Number(item.id_cargo) === positionId)) {
				setSelectedTransferPosition(positionId);
			}
			return;
		}
		setSelectedPosition('');
		setPositions([]);
		setSelectedPositionSector(targetSectorId);
		const list = await getPositions(String(targetSectorId));
		if (list.some(item => Number(item.id_cargo) === positionId)) {
			setSelectedPosition(positionId);
		}
	};

	const handleChangeManualPosition = (checked: boolean) => {
		setManualPositionChange(checked);
		setSelectedContractChange(undefined);
	};

	const getTransferSectors = async () => {
		const workplace = props.comparation?.esocialValues?.workplace;
		if (!workplace) {
			setSectorsLoaded(true);
			return;
		}
		setLoadingSectors(true);
		const response = await comparationService.getSectorOptions(props.comparation!.id, workplace);
		setTransferSectors(response && response.data ? response.data : []);
		setSectorsLoaded(true);
		setLoadingSectors(false);
	};

	const getTransferPositions = async (sectorId: string) => {
		setLoadingTransferPositions(true);
		const response = await employeeService.getPositionsBySector(sectorId);
		const list = response && response.data ? response.data : [];
		setTransferPositions(list);
		setLoadingTransferPositions(false);
		return list;
	};

	const handleChangeTransferSector = (sectorId: string) => {
		setSelectedTransferSector(sectorId);
		setSelectedTransferPosition('');
		setTransferPositions([]);
		getTransferPositions(sectorId);
	};

	const getSggAbsences = async () => {
		if (!props.comparation) {
			return;
		}
		setLoadingSggAbsences(true);
		const response = await comparationService.getAbsences(props.comparation.id);
		setSggAbsences(response && response.data ? response.data : []);
		setSggAbsencesLoaded(true);
		setLoadingSggAbsences(false);
	};

	const absenceLabel = (absence: AbsenceOption) => {
		const start = absence.startDate || 'Sem data de início';
		const reason = absence.reason || 'Motivo não informado';
		return absence.possibleReturnDate
			? `${start} - ${reason} (retorno previsto: ${absence.possibleReturnDate})`
			: `${start} - ${reason}`;
	};

	const getExamValidation = async () => {
		if (!props.comparation) {
			return;
		}
		setLoadingExam(true);
		const response = await comparationService.getDismissalExamValidation(props.comparation.id);
		setExamValidation(response && response.data ? response.data : undefined);
		setExamChecked(true);
		setLoadingExam(false);
	};

	const getRiskChange = async (positionId: number | string, sectorId?: number | string) => {
		if (!props.comparation || !positionId || positionId === CREATE_POSITION) {
			setRiskChange(undefined);
			return;
		}
		setLoadingRiskChange(true);
		const response = await comparationService.getRiskChangeValidation(props.comparation.id,
			Number(positionId), sectorId ? Number(sectorId) : undefined);
		setRiskChange(response && response.data ? response.data : undefined);
		setLoadingRiskChange(false);
	};

	const update = async () => {
		if (!props.comparation) {
			return;
		}
		setSaving(true);
		const response = await comparationService.sync(props.comparation.id, {
			fields: selectedFields,
			absenceEventId: selectedAbsence,
			contractChangeEventId: selectedContractChange,
			dismissalEventId: selectedDismissal,
			positionId: selectedPosition ? Number(selectedPosition) : undefined,
			positionSectorId: selectedPositionSector ? Number(selectedPositionSector) : undefined,
			manualPositionChange: manualPositionChange,
			transferSectorId: selectedTransferSector ? Number(selectedTransferSector) : undefined,
			transferPositionId: selectedTransferPosition ? Number(selectedTransferPosition) : undefined,
			sggAbsenceId: selectedSggAbsence ? Number(selectedSggAbsence) : undefined
		});
		setSaving(false);
		if (!response || response.status !== 200) {
			showError(response?.data?.error);
			return;
		}
		showAlert('Funcionário atualizado no SGG com sucesso!');
		setOpen(false);
		props.onSynced();
	};

	useEffect(() => {
		setSelectedFields([]);
		setSelectedAbsence(undefined);
		setSelectedContractChange(undefined);
		setSelectedDismissal(undefined);
		setSelectedPosition('');
		setPositions([]);
		setPositionSectors([]);
		setSelectedPositionSector('');
		setPositionSectorsLoaded(false);
		setManualPositionChange(false);
		setCreatePositionOpen(false);
		setTransferSectors([]);
		setTransferPositions([]);
		setSelectedTransferSector('');
		setSelectedTransferPosition('');
		setSectorsLoaded(false);
		setSggAbsences([]);
		setSelectedSggAbsence('');
		setSggAbsencesLoaded(false);
		setExamValidation(undefined);
		setExamChecked(false);
		setRiskChange(undefined);
	}, [props.comparation?.id]);

	useEffect(() => {
		if (absenceReturn && !sggAbsencesLoaded && !loadingSggAbsences) {
			getSggAbsences();
		}
	}, [selectedFields]);

	useEffect(() => {
		if (transfer && !sectorsLoaded && !loadingSectors) {
			getTransferSectors();
		}
	}, [selectedFields]);

	useEffect(() => {
		if (positionSelected && !positionSectorsLoaded && !loadingPositionSectors) {
			getPositionSectors();
		}
	}, [selectedFields]);

	useEffect(() => {
		if (dismissal && !examChecked && !loadingExam) {
			getExamValidation();
		}
	}, [selectedFields]);

	useEffect(() => {
		getRiskChange(selectedPosition, selectedPositionSector);
	}, [selectedPosition]);

	useEffect(() => {
		getRiskChange(selectedTransferPosition, selectedTransferSector);
	}, [selectedTransferPosition]);

	const fields = divergentFields();
	const activeGroup = selectedFields.length ? fieldGroup(selectedFields[0]) : undefined;
	const absences = changesByType('ABSENCE');
	const contractChanges = changesByType('CONTRACT_CHANGE');
	const dismissals = changesByType('DISMISSAL');
	const esocialSituation = normalize(props.comparation?.esocialValues?.situation);
	const sggSituation = normalize(props.comparation?.sggValues?.situation);
	const situationSelected = selectedFields.includes('situation');
	const positionSelected = selectedFields.includes('position') || selectedFields.includes('cbo');
	const dismissal = situationSelected && dismissedSituations.includes(esocialSituation);
	const absence = situationSelected && esocialSituation === 'afastado';
	const absenceReturn = situationSelected && !dismissal && !absence
		&& sggSituation === 'afastado' && esocialSituation !== 'afastado';
	const absenceEvents = absenceReturn
		? absences.filter(item => !!item.absenceEndDate)
		: absences;
	const missingSggAbsences = absenceReturn && sggAbsencesLoaded && !sggAbsences.length;
	const absenceSelectLabel = 'Afastamento do SGG que receberá a data de retorno';
	const absenceInfo = absenceReturn
		? 'Selecione o afastamento do eSocial que possui a data de retorno.'
			+ ' Essa data será registrada no afastamento do SGG escolhido abaixo'
		: 'Selecione o afastamento do eSocial para sincronizar. O afastamento selecionado será atualizado no SGG';
	const absenceEmptyMessage = absenceReturn
		? 'Nenhum afastamento do eSocial com data de retorno foi extraído para este funcionário.'
			+ ' A data de retorno do eSocial é obrigatória para registrar o retorno no SGG,'
			+ ' então esta atualização não pode ser realizada.'
		: 'Nenhum afastamento do eSocial foi extraído para este funcionário.'
			+ ' A data de afastamento do eSocial é obrigatória para registrar o afastamento no SGG,'
			+ ' então esta atualização não pode ser realizada.';
	const transfer = selectedFields.includes('workplace');
	const candidateRemoval = selectedFields.includes('registration')
		&& !normalize(props.comparation?.sggValues?.registration)
		&& !!normalize(props.comparation?.esocialValues?.registration);
	const missingTransferSectors = transfer && sectorsLoaded && !transferSectors.length;
	const positionChange = positionSelected && !manualPositionChange;
	const showContractChanges = positionChange || transfer;
	const contractChangeAction = transfer ? 'a transferência' : 'a alteração de cargo';
	const contractChangeInfo = dismissal
		? 'Escolha a movimentação trabalhista para a atualização do cargo no SGG'
		: `Escolha a movimentação trabalhista que originou ${contractChangeAction}.`
			+ ` A data dela será usada como data d${transfer ? 'a transferência' : 'a alteração'} no SGG`;
	const contractChangeEmptyMessage = dismissal
		? 'Nenhuma movimentação trabalhista do eSocial foi extraída para este funcionário.'
			+ ' A atualização será realizada sem essa informação.'
		: 'Nenhuma movimentação trabalhista do eSocial foi extraída para este funcionário.'
			+ ` A data da movimentação é obrigatória para registrar ${contractChangeAction} no SGG,`
			+ ' então esta atualização não pode ser realizada.';
	const missingPositionSectors = positionSelected && positionSectorsLoaded && !positionSectors.length;
	const esocialCbo = (props.comparation?.esocialValues?.cbo || '').split(' - ')[0].trim();
	const missingEsocialCbo = !esocialCbo.replace(/\D/g, '');
	const positionSelectLabel = `Novo cargo no SGG (com o CBO ${esocialCbo || '-'})`;
	const positionSectorLabel = manualPositionChange ? 'Novo setor no SGG' : 'Setor do funcionário no SGG';
	const canUpdate = !!selectedFields.length
		&& !loadingExam
		&& (!(absence || absenceReturn) || !!selectedAbsence)
		&& (!absenceReturn || (!loadingSggAbsences && !!selectedSggAbsence))
		&& (!(transfer || positionChange) || !!selectedContractChange)
		&& (!transfer || (!!selectedTransferSector && !!selectedTransferPosition))
		&& (!dismissal || !dismissals.length || !!selectedDismissal)
		&& (!showContractChanges || !contractChanges.length || !!selectedContractChange)
		&& (!positionSelected || (!!selectedPositionSector && !!selectedPosition));
	const riskChangeAlert = <>
		{loadingRiskChange && <Box className="exam-check">
			<CircularProgress size="3vh"/>
			<Typography variant="body2">Verificando a mudança de riscos do cargo...</Typography>
		</Box>}
		{!loadingRiskChange && riskChange?.riskChange && <Alert
			severity="warning"
			variant="filled"
			icon={<Warning/>}
			className="risk-alert"
		>
			{riskChange.message}
		</Alert>}
	</>;
	const positionSelects = <Box className="transfer-select">
		{loadingPositionSectors && <CircularProgress size="3vh"/>}
		{missingPositionSectors && <Alert severity="warning" variant="outlined">
			Não há setores cadastrados no SGG para a empresa do funcionário
			({props.comparation?.sggValues?.workplace || '-'}).
		</Alert>}
		{!loadingPositionSectors && !!positionSectors.length && <>
			<FormControl fullWidth>
				<InputLabel>{positionSectorLabel}</InputLabel>
				<Select
					label={positionSectorLabel}
					id="position-sector"
					value={selectedPositionSector}
					onChange={(e) => handleChangePositionSector(String(e.target.value))}
				>
					{positionSectors.map(item => (
						<MenuItem key={item.id_setor} value={item.id_setor}>
							<Box className="sector-option">
								<span>{item.setor}</span>
								<span className={item.positionsWithCbo ? 'sector-count' : 'sector-count-empty'}>({item.positionsWithCbo} cargos com CBO atual)</span>
								{item.current && <span className="sector-current">(setor atual)</span>}
							</Box>
						</MenuItem>
					))}
				</Select>
			</FormControl>
			{!!selectedPositionSector && <FormControl fullWidth>
				<InputLabel>{positionSelectLabel}</InputLabel>
				<Select
					label={positionSelectLabel}
					id="sgg-position"
					value={selectedPosition}
					onChange={(e) => handleSelectPosition(String(e.target.value), false)}
					disabled={loadingPositions}
				>
					{positions.map(position => {
						const esocialName = matchesEsocialPosition(position);
						return <MenuItem
							key={position.id_cargo}
							value={position.id_cargo}
							className={esocialName ? 'esocial-position-option' : ''}
						>
							<span className={esocialName ? 'esocial-position-name' : ''}>
								{position.cargo}
							</span>
						</MenuItem>;
					})}
					<MenuItem value={CREATE_POSITION} className="create-position-option">
						<AddCircleOutline fontSize="small"/> Criar novo cargo a partir de um existente
					</MenuItem>
				</Select>
			</FormControl>}
			{riskChangeAlert}
		</>}
	</Box>;

	const actions = () => {
		const list: string[] = [];
		if (dismissal) {
			list.push('DISMISSAL');
		}
		if (absence) {
			list.push('ABSENCE');
		}
		if (absenceReturn) {
			list.push('ABSENCE_RETURN');
		}
		if (positionChange) {
			list.push('POSITION_CHANGE');
		}
		if (selectedFields.includes('workplace')) {
			list.push('TRANSFER');
		}
		const updateFields = selectedFields.filter(field => field !== 'situation' && field !== 'position'
			&& field !== 'workplace' && field !== 'cbo'
			&& !(dismissal && field === 'dismissalDate'));
		if (manualPositionChange && positionSelected) {
			list.push('MANUAL_POSITION_CHANGE');
		} else if (updateFields.length) {
			list.push('UPDATE');
		}
		if (candidateRemoval) {
			list.push('CANDIDATE_REMOVAL');
		}
		return list;
	};

	return (
		<Dialog
			open={open}
			onClose={() => setOpen(false)}
			className="modal-divergences"
			fullWidth
			maxWidth="md"
		>
			<DialogTitle>Divergências de {props.comparation?.name}</DialogTitle>
			<DialogContent>
				{!!fields.length && <Alert severity="info" variant="outlined" className="info-message">
					Selecione as alterações que você deseja atualizar diretamente no SGG.
					É permitida uma ação por atualização, então ao marcar um campo os que geram
					outra ação ficam indisponíveis.
				</Alert>}
				{!fields.length
					? <Typography>Nenhuma divergência de campo para exibir.</Typography>
					: <TableContainer component={Paper}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell><strong>Campo</strong></TableCell>
									<TableCell><strong>SGG</strong></TableCell>
									<TableCell><strong>eSocial</strong></TableCell>
									<TableCell align="center"><strong>Atualizar</strong></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{fields.map(field => (
									<TableRow key={field.key}>
										<TableCell className="field-label">{field.label}</TableCell>
										<TableCell>{formatValue(field.key, props.comparation?.sggValues?.[field.key])}</TableCell>
										<TableCell>{formatValue(field.key, props.comparation?.esocialValues?.[field.key])}</TableCell>
										<TableCell align="center">
											<Tooltip title={fieldTooltip(field.key)}>
												<span>
													<Switch
														checked={selectedFields.includes(field.key)}
														disabled={isFieldLocked(field.key)
															|| (!!activeGroup && activeGroup !== fieldGroup(field.key))}
														onChange={() => toggleField(field.key)}
													/>
												</span>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				}
				{!!actions().length && <Box className="sync-actions">
					<Typography className="sync-actions-title" variant="subtitle2">Ações que serão realizadas</Typography>
					{actions().map(action => (
						<Paper key={action} className="sync-action">
							<Typography>{actionMessages[action]}</Typography>
						</Paper>
					))}
				</Box>}
				{positionSelected && <Paper className="manual-position" variant="outlined">
					<FormControlLabel
						control={<Switch
							checked={manualPositionChange}
							onChange={(e) => handleChangeManualPosition(e.target.checked)}
						/>}
						label="Alterar o cargo sem movimentação trabalhista do eSocial"
					/>
					<Typography variant="body2" className="manual-position-info">
						Ative quando não houver evento do eSocial para a troca. O setor e o cargo escolhidos
						serão gravados direto no cadastro do funcionário, sem registrar a alteração de cargo
						no SGG.
					</Typography>
				</Paper>}
				{manualPositionChange && positionSelected && positionSelects}
				{dismissal && loadingExam && <Box className="exam-check">
					<CircularProgress size="3vh"/>
					<Typography variant="body2">Validando a necessidade de exame ocupacional...</Typography>
				</Box>}
				{dismissal && !loadingExam && examValidation?.needsExam && <Alert
					severity="error"
					variant="filled"
					icon={<MedicalServices/>}
					className="exam-alert"
				>
					{examValidation.message}
				</Alert>}
				{dismissal && <ComparationEventSelector
					info="Selecione o desligamento do eSocial para sincronizar. O motivo dele será enviado ao SGG"
					emptyMessage="Nenhum desligamento do eSocial foi extraído para este funcionário. O desligamento será registrado no SGG sem o motivo."
					events={dismissals}
					selected={selectedDismissal}
					onSelect={setSelectedDismissal}
				/>}
				{(absence || absenceReturn) && <ComparationEventSelector
					info={absenceInfo}
					emptyMessage={absenceEmptyMessage}
					events={absenceEvents}
					selected={selectedAbsence}
					onSelect={setSelectedAbsence}
				>
					{absenceReturn && <Box className="absence-select">
						{loadingSggAbsences && <CircularProgress size="3vh"/>}
						{missingSggAbsences && <Alert severity="warning" variant="outlined">
							Não há afastamentos sem data de retorno no SGG para este funcionário.
						</Alert>}
						{!loadingSggAbsences && !!sggAbsences.length && <FormControl fullWidth>
							<InputLabel>{absenceSelectLabel}</InputLabel>
							<Select
								label={absenceSelectLabel}
								id="sgg-absence"
								value={selectedSggAbsence}
								onChange={(e) => setSelectedSggAbsence(e.target.value)}
							>
								{sggAbsences.map(item => (
									<MenuItem key={item.id} value={item.id}>
										{absenceLabel(item)}
									</MenuItem>
								))}
							</Select>
						</FormControl>}
					</Box>}
				</ComparationEventSelector>}
				{showContractChanges &&
				<ComparationEventSelector
					info={contractChangeInfo}
					emptyMessage={contractChangeEmptyMessage}
					events={contractChanges}
					selected={selectedContractChange}
					onSelect={setSelectedContractChange}
				>
					{positionChange && positionSelects}
					{transfer && <Box className="transfer-select">
						{loadingSectors && <CircularProgress size="3vh"/>}
						{missingTransferSectors && <Alert severity="warning" variant="outlined">
							Não há setores cadastrados no SGG para a empresa de destino
							({props.comparation?.esocialValues?.workplace || '-'}).
						</Alert>}
						{!loadingSectors && !!transferSectors.length && <>
							<FormControl fullWidth>
								<InputLabel>Setor na empresa de destino</InputLabel>
								<Select
									label="Setor na empresa de destino"
									id="transfer-sector"
									value={selectedTransferSector}
									onChange={(e) => handleChangeTransferSector(String(e.target.value))}
								>
									{transferSectors.map(item => (
										<MenuItem key={item.id_setor} value={item.id_setor}>
											<Box className="sector-option">
												<span>{item.setor}</span>
												<span className={item.positionsWithCbo ? 'sector-count' : 'sector-count-empty'}>({item.positionsWithCbo} cargos com CBO atual)</span>
												{item.current && <span className="sector-current">(setor atual)</span>}
											</Box>
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl fullWidth>
								<InputLabel>Cargo na empresa de destino</InputLabel>
								<Select
									label="Cargo na empresa de destino"
									id="transfer-position"
									value={selectedTransferPosition}
									onChange={(e) => handleSelectPosition(String(e.target.value), true)}
									disabled={!selectedTransferSector || loadingTransferPositions}
								>
									{transferPositions.map(item => {
										const esocialName = matchesEsocialPosition(item);
										return <MenuItem
											key={item.id_cargo}
											value={item.id_cargo}
											className={esocialName ? 'esocial-position-option' : ''}
										>
											<span className={esocialName ? 'esocial-position-name' : ''}>
												{`${item.cargo} - ${item.funcao} - ${item.CBO}`}
											</span>
										</MenuItem>;
									})}
									<MenuItem value={CREATE_POSITION} className="create-position-option">
										<AddCircleOutline fontSize="small"/> Criar novo cargo a partir de um existente
									</MenuItem>
								</Select>
							</FormControl>
							{riskChangeAlert}
						</>}
					</Box>}
				</ComparationEventSelector>}
			</DialogContent>
			<ModalCreatePosition
				comparation={props.comparation}
				cnpj={createPositionTransfer
					? props.comparation?.esocialValues?.workplace
					: props.comparation?.sggValues?.workplace}
				sectors={createPositionTransfer ? transferSectors : positionSectors}
				open={createPositionOpen}
				onClose={() => setCreatePositionOpen(false)}
				onCreated={handleCreatedPosition}
			/>
			<DialogActions>
				<Button onClick={() => setOpen(false)}>Fechar</Button>
				{!!selectedFields.length && <Button
					variant="contained"
					color="success"
					onClick={update}
					disabled={!canUpdate || saving}
				>
					{saving ? <CircularProgress size="3vh"/> : 'Atualizar'}
				</Button>}
			</DialogActions>
		</Dialog>
	);
}

export default ModalDivergences;